import { Box, CircularProgress } from '@mui/material';
import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';

const DEFAULT_ESTIMATION = Object.freeze({ estimatedRecords: 0, sourceYears: [] });
const PagePresentationContext = createContext(null);
export const PAGE_LOADER_DELAY_MS = 200;
export const PAGE_LOADER_MIN_VISIBLE_MS = 450;

export function useDelayedLoading(
  active,
  delayMs = PAGE_LOADER_DELAY_MS,
  minVisibleMs = PAGE_LOADER_MIN_VISIBLE_MS,
) {
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef(null);

  useEffect(() => {
    let timer;

    if (active) {
      if (!visible) {
        timer = window.setTimeout(() => {
          shownAtRef.current = Date.now();
          setVisible(true);
        }, delayMs);
      }
    } else if (visible) {
      const elapsed = shownAtRef.current ? Date.now() - shownAtRef.current : minVisibleMs;
      timer = window.setTimeout(() => {
        shownAtRef.current = null;
        setVisible(false);
      }, Math.max(0, minVisibleMs - elapsed));
    }

    return () => window.clearTimeout(timer);
  }, [active, delayMs, minVisibleMs, visible]);

  return visible;
}

export function PagePresentationProvider({ children }) {
  const loadedOnceRef = useRef(false);
  const [state, setState] = useState({
    initialLoading: false,
    refreshLoading: false,
    status: null,
    estimation: DEFAULT_ESTIMATION,
  });

  const report = useCallback(({ loading, status, estimation }) => {
    const hasLoaded = loadedOnceRef.current;
    setState({
      initialLoading: Boolean(loading) && !hasLoaded,
      refreshLoading: Boolean(loading) && hasLoaded,
      status: status || null,
      estimation: estimation || DEFAULT_ESTIMATION,
    });
    if (!loading) loadedOnceRef.current = true;
  }, []);

  const value = useMemo(() => ({ ...state, report }), [report, state]);
  return (
    <PagePresentationContext.Provider value={value}>
      {children}
    </PagePresentationContext.Provider>
  );
}

export function usePagePresentation({ loading, status = null, estimation = null }) {
  const context = useContext(PagePresentationContext);
  const report = context?.report;
  const estimatedRecords = Number(estimation?.estimatedRecords) || 0;
  const sourceYears = Array.isArray(estimation?.sourceYears) ? estimation.sourceYears : [];
  const sourceYearsKey = sourceYears.join(',');

  useLayoutEffect(() => {
    const reportedSourceYears = sourceYearsKey
      ? sourceYearsKey.split(',').map(Number).filter(Number.isFinite)
      : [];
    report?.({
      loading,
      status,
      estimation: { estimatedRecords, sourceYears: reportedSourceYears },
    });
  }, [estimatedRecords, loading, report, sourceYearsKey, status]);
}

export function usePagePresentationState() {
  return useContext(PagePresentationContext) || {
    initialLoading: false,
    refreshLoading: false,
    status: null,
    estimation: DEFAULT_ESTIMATION,
  };
}

export function PageLoadingBoundary({ children, label }) {
  const { initialLoading } = usePagePresentationState();
  const loaderVisible = useDelayedLoading(initialLoading);

  return (
    <Box
      aria-busy={initialLoading || loaderVisible}
      sx={{
        minHeight: initialLoading || loaderVisible
          ? '100dvh'
          : 0,
        position: 'relative',
      }}
    >
      {children}
      {(initialLoading || loaderVisible) && (
        <Box
          sx={(theme) => ({
            alignItems: 'center',
            backdropFilter: 'blur(2px)',
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(15, 23, 42, 0.58)'
              : 'rgba(248, 250, 252, 0.72)',
            display: 'flex',
            justifyContent: 'center',
            bottom: 0,
            left: 0,
            position: 'fixed',
            right: 0,
            top: 0,
            zIndex: 40,
          })}
        >
          {loaderVisible && (
            <CircularProgress
              aria-label={label}
              color="primary"
              size={38}
              thickness={4}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
